import { get, isFunction, isEmpty } from 'lodash';
import { Cookies } from 'react-cookie';
import i18n from 'i18n';
import requester from 'utils/requester';
import api from 'config/api';
import storage from 'helpers/storage';
import apiCubic from 'config/api-cubic';
import { USER_FLAGS } from 'config';
import SpatialNavigationWrapper from 'components/navigation/SpatialNavigationWrapper';
import { hideLoader, showLoader } from 'actions/loader';
import { hideModal, showModal } from 'actions/modal';
import { getProfileData } from 'reducers/profile/selectors';
import { MODAL_TYPES } from 'constants/modal-types';
import { getCurrentLangVoliaAPICode, INIT_UI_LANGUAGE } from 'constants/languages';
import { fetchMenu } from 'actions/menu';
import { backToPageThatCausedAuth, patchUserProfileLanguage, resetUserData } from 'actions/app';
import {
  collectLoginFormData,
  getPlatformParams,
  isValidLoginType,
  LOGIN_ERROR_CODES,
  loginWithToken,
  parseLoginErrorMessage,
  parseRestorePasswordErrorMessage,
  resetLoginCookies,
  voliaExtraLogin,
  isExpiredError,
  LOGIN_TYPES,
} from './helpers';
import innerAnalyticsVolia from 'helpers/innerAnalyticsVolia';

import * as types from './types';

import Platform from 'platform';
import { showToast } from 'actions/toast';
import loginDataService from 'utils/login-data-service';

export const loginUser = (params) => async (dispatch, _, history) => {
  const { login, password, loginType, code, userFlag, errorModal, onSuccess, withLoader = true } = params;
  if (!isValidLoginType(loginType)) throw new Error('Not valid login type');

  dispatch({ type: types.LOGIN });
  if (withLoader) dispatch(showLoader());
  try {
    let loginData;

    if (loginType !== LOGIN_TYPES.code) {
      loginData = await requester({
        method: 'post',
        url: `${api.host}${api.endpoints.login}`,
        appendParams: false,
        params: userFlag !== USER_FLAGS.LIFECELL ? { external: 'Volia' } : null,
        data: collectLoginFormData({ login, password, loginType }),
      });

      if (userFlag === USER_FLAGS.LIFECELL && loginType === 'phone') {
        innerAnalyticsVolia.postEvent({
          eventGroup: 'sign in screen',
          eventName: 'sign in via phone lifecell',
          eventValue: 'success',
          addInfo: login,
        });
      } else {
        innerAnalyticsVolia.postEvent({
          eventGroup: 'alternative sign in screen',
          eventName: loginType === LOGIN_TYPES.email ? 'sign in via email' : 'sign in via contract',
          eventValue: 'success',
          addInfo: login,
        });
      }
    }

    // https://hootko.atlassian.net/browse/STVWL-518
    const data = await voliaExtraLogin(code || loginData.customerRef);

    dispatch({
      type: types.LOGIN_SUCCESS,
      payload: data,
    });

    if (userFlag) {
      const phone = userFlag === USER_FLAGS.LIFECELL ? login : null;
      loginDataService.set({ phone, userFlag });
      dispatch(setLoginData({ phone, userFlag }));
    }
    // Меню и ссылки в нем отличаються для залогиненного и не залогиненного пользователя
    // обновить каналы после логина
    await dispatch(fetchMenu());
    await dispatch(resetUserData());

    if (isFunction(onSuccess)) {
      await onSuccess(data);
    } else {
      dispatch(backToPageThatCausedAuth());
      // Timeout is necessary here
      setTimeout(() => history.push('/profile/packages/all'), 100);
    }
    // If logged in as a new user, we need to restore default flag
    dispatch(setIsUsingDefaultPin(true));
  } catch (error) {
    const errorCode = get(error, ['response', 'data', 'errorCode']);
    const message = get(error, ['response', 'data', 'message'], error.message);
    const exception = get(error, ['response', 'data', 'exception']);

    const isSeveralAccounts = [
      LOGIN_ERROR_CODES.PHONE_SEVERAL_CONTRACTS,
      LOGIN_ERROR_CODES.EMAIL_SEVERAL_CONTRACTS,
    ].includes(errorCode);

    if (userFlag === USER_FLAGS.LIFECELL && loginType === 'phone') {
      innerAnalyticsVolia.postEvent({
        eventGroup: 'sign in screen',
        eventName: 'sign in via phone lifecell',
        eventValue: exception,
        addInfo: login,
      });
    } else {
      innerAnalyticsVolia.postEvent({
        eventGroup: 'alternative sign in screen',
        eventName: loginType === 'email' ? 'sign in via email' : 'sign in via contract',
        eventValue: errorCode,
        addInfo: login,
      });
    }

    // Hack:
    // Volia has aprrox 700 user with email as contract number
    // So if tried to login with email and got 'login_error_2' - we should try another login with contract type.
    if (loginType === 'email' && errorCode === 'login_error_2') {
      await dispatch(loginUser({ loginType: 'contract', login, password, code, errorModal, onSuccess, withLoader }));
      return;
    }
    // Нужно отдельно обрабатывать ошибку с несколькими аккаунтами
    // привязанными к одному логину
    if (isSeveralAccounts) {
      const { title, description, loginValue: highlightedValue } = parseLoginErrorMessage(message);

      dispatch(
        showModal(MODAL_TYPES.CONFIRM, {
          title,
          description,
          highlightedValue,
          confirmText: i18n.t('common.tryAnotherLoginMethod'),
          rejectText: i18n.t('common.cancel'),
          onModalBack() {
            dispatch(hideModal());
            dispatch(hideLoader());
            errorModal.returnFocusTo && SpatialNavigationWrapper.focus(errorModal.returnFocusTo);
          },
          onModalConfirm() {
            dispatch(hideModal());
            history.push(`/profile/authorization`);
          },
        }),
      );
      return;
    }
    dispatch(onLoginError(message));
    resetLoginCookies();

    if (errorModal) {
      const options = {
        message: exception === 'BadCredentialsException' ? i18n.t('authorizationLoginOTPPage.incorrectCode') : message,
        confirmText: i18n.t('common.restorePassword'),
        rejectText: i18n.t('common.cancel'),
        onModalConfirm: () => {
          dispatch(hideModal());
          history.push('/profile/authorization/restore/password');
        },
        onModalBack() {
          dispatch(hideModal());
          dispatch(hideLoader());
          errorModal.returnFocusTo && SpatialNavigationWrapper.focus(errorModal.returnFocusTo);
        },
      };

      dispatch(showModal(MODAL_TYPES.CONFIRM, options));
    }
  } finally {
    if (withLoader) dispatch(hideLoader());
  }
};

const onLoginError = (payload) => ({
  type: types.LOGIN_ERROR,
  payload,
});

export const logoutUser = () => async (dispatch) => {
  dispatch({ type: types.LOGOUT });
  dispatch(showLoader());

  try {
    await requester({
      method: 'post',
      url: `${api.host}${api.endpoints.logout}`,
      appendParams: false,
    });
    resetLoginCookies();
    loginDataService.clear();
    innerAnalyticsVolia.postEvent({ eventGroup: 'profile', eventName: 'sign out' });

    dispatch({ type: types.LOGOUT_SUCCESS });
    // Меню и ссылки в нем отличаються для
    // залогиненного и не залогиненного пользователя
    await dispatch(fetchMenu());
    dispatch(resetUserData());
  } catch (error) {
    dispatch({ type: types.LOGOUT_ERROR, payload: error });
  } finally {
    dispatch(hideLoader());
  }
};

export const fetchProfile = () => async (dispatch) => {
  const token = new Cookies().get('auto-provision-token');
  if (!token) return;

  try {
    dispatch(showLoader());
    dispatch({ type: types.FETCH });
    const profile = await loginWithToken(token);
    const userLanguage = get(profile, 'users[0].language');

    await requester({
      method: 'post',
      url: `${api.host}${api.endpoints.follow}`,
      appendParams: false,
      data: getPlatformParams(),
    });

    dispatch(onFetchProfileSuccess(profile));

    if (userLanguage !== getCurrentLangVoliaAPICode()) {
      dispatch(patchUserProfileLanguage(getCurrentLangVoliaAPICode()));
    }

    dispatch(fetchBalance());
  } catch (error) {
    dispatch(onFetchProfileError(error));
  } finally {
    dispatch(hideLoader());
  }
};

export const loginAfterRegistration =
  ({ password, phone, email, contractNumber, errorModal, onSuccess }) =>
  async (dispatch) => {
    let credentials = {};
    /*
     * Хак из-за того, что регистрация переиспользуется для возобновления пароля!
     * Если есть номер контракта, то лучше делать логин через него,
     * т.к. мы можем получить модальное окно с придупреждением, что у этого
     * пользователя есть несколько контрактов и предложением ввести номер контракта
     * что абсолютно лишняя операция, если этот номер у нас есть
     * (и скорее всего пользователь его уже вводил, если это было восстановление пароля)
     */
    if (contractNumber) {
      credentials = { login: contractNumber, loginType: 'contract' };
    } else if (phone) {
      /*
       * По требованиям маска должна содержать часть телефона
       * (а именно первый "0"), поэтому к самому номеру нужно
       * его добавить для формата "xxx - xxx - xx - xx"
       */
      credentials = { login: `0${phone}`, loginType: 'phone' };
    } else if (email) {
      credentials = { login: email, loginType: 'email' };
    }

    try {
      const data = { ...credentials, password, errorModal, onSuccess };

      await dispatch(loginUser(data));
    } catch (error) {
      console.error(error);
      const message = get(error, ['response', 'data', 'message'], error.message);
      dispatch(
        showModal(MODAL_TYPES.INFO, {
          onModalBack() {
            dispatch(hideModal());
            errorModal.returnFocusTo && SpatialNavigationWrapper.focus(errorModal.returnFocusTo);
          },
          message,
        }),
      );
    }
  };

export const sendRestorePasswordOTP = (payload) => async (dispatch, _, history) => {
  const { phone, email, contractId, errorModal } = payload;
  try {
    dispatch(showLoader());
    const authToken = apiCubic.getAuthToken();

    const { data, error } = await requester({
      method: 'post',
      url: `${apiCubic.host}${apiCubic.endpoints.sendOTP}`,
      appendParams: false,
      extendHeadersWithSession: false,
      headers: { Authorization: `Basic ${authToken}` },
      data: {
        login: email || `0${phone}`,
        loginType: email ? 2 : 1,
        contractId: contractId,
        language: INIT_UI_LANGUAGE,
      },
    });

    if (error) throw new Error(error.message);

    if (data.payload.otpSent) {
      // https://hootko.atlassian.net/browse/STVWL-420
      const contract = (data.payload.voliaContractCodes || data.payload.dataLogins)[0];
      const contractNumber = contract?.login;
      const contractId = contract?.contractId;

      history.push({
        pathname: '/profile/authorization/restore/password/otp',
        search: `?${new URLSearchParams({ contractId, contractNumber })}`,
      });

      return;
    }

    if (!isEmpty(data.payload.voliaContractCodes) || !isEmpty(data.payload.dataLogins)) {
      history.push({
        pathname: '/profile/authorization/restore/password/contract',
        state: {
          message: parseRestorePasswordErrorMessage(data.payload.userMessage),
          contracts: [...data.payload.voliaContractCodes, ...data.payload.dataLogins],
        },
      });
    }
  } catch (error) {
    const message = get(error.response, ['data', 'message'], error.message);

    dispatch(
      showModal('INFO', {
        onModalBack() {
          dispatch(hideModal());
          errorModal.returnFocusTo && SpatialNavigationWrapper.focus(errorModal.returnFocusTo);
        },
        message,
      }),
    );
  } finally {
    dispatch(hideLoader());
  }
};

export const sendOTP = (payload) => async (dispatch, _, history) => {
  const { otp, phone, email, contractId, errorModal } = payload;

  try {
    dispatch(showLoader());
    const authToken = apiCubic.getAuthToken();

    const { error } = await requester({
      method: 'post',
      url: `${apiCubic.host}${apiCubic.endpoints.changePass}`,
      appendParams: false,
      extendHeadersWithSession: false,
      headers: { Authorization: `Basic ${authToken}` },
      data: {
        login: email || `0${phone}`,
        loginType: email ? 2 : 1,
        contractId: contractId,
        otp: otp,
        language: INIT_UI_LANGUAGE,
      },
    });

    if (error) throw new Error(error.message);

    history.push('/profile/authorization/restore/password/set-new');
  } catch (error) {
    const message = get(error.response, ['data', 'message'], error.message);

    dispatch(
      showModal(MODAL_TYPES.INFO, {
        onModalBack() {
          dispatch(hideModal());
          errorModal.returnFocusTo && SpatialNavigationWrapper.focus(errorModal.returnFocusTo);
        },
        message,
      }),
    );
  } finally {
    dispatch(hideLoader());
  }
};

export const setNewPassword = (payload) => async (dispatch, _, history) => {
  const { otp, password, phone, email, contractId, contractNumber, isSeveralContracts, errorModal } = payload;

  try {
    dispatch(showLoader());
    const authToken = apiCubic.getAuthToken();

    const { error } = await requester({
      method: 'post',
      url: `${apiCubic.host}${apiCubic.endpoints.changePass}`,
      appendParams: false,
      extendHeadersWithSession: false,
      headers: { Authorization: `Basic ${authToken}` },
      data: {
        login: email || `0${phone}`,
        loginType: email ? 2 : 1,
        contractId: contractId,
        otp: otp,
        password: password,
        language: INIT_UI_LANGUAGE,
      },
    });

    if (error && isExpiredError(error)) {
      const message = i18n.t('common.sessionExpired');
      dispatch(
        showModal(MODAL_TYPES.INFO, {
          onModalBack() {
            // We need to back to the first auth page and wipe history state
            // if "isSeveralContracts" = true, that means user been on select contract page
            // and we should add one more step to wipe
            const steps = isSeveralContracts ? 6 : 5;
            dispatch(hideModal());
            history.go(-steps);
            errorModal.returnFocusTo && SpatialNavigationWrapper.focus(errorModal.returnFocusTo);
          },
          message,
        }),
      );
      return;
    }

    if (error) throw new Error(error.message);

    const toastText = i18n.t('authorizationRestorePasswordSetNewPage.success');

    const onSuccess = () => {
      dispatch(backToPageThatCausedAuth());
      // Timeout is necessary here
      setTimeout(() => history.push('/'), 100);
    };

    await dispatch(
      loginAfterRegistration({
        password,
        phone,
        email,
        contractNumber,
        errorModal,
        onSuccess,
      }),
    );
    dispatch(backToPageThatCausedAuth());
    dispatch(showToast({ options: { toastText } }));
  } catch (error) {
    const message = get(error.response, ['data', 'message'], error.message);

    dispatch(
      showModal(MODAL_TYPES.INFO, {
        onModalBack() {
          dispatch(hideModal());
          errorModal.returnFocusTo && SpatialNavigationWrapper.focus(errorModal.returnFocusTo);
        },
        message,
      }),
    );
  } finally {
    dispatch(hideLoader());
  }
};

export const pinRecovery = (payload) => async (dispatch, _, history) => {
  const { email, phone, contractNumber, errorModal } = payload;
  try {
    dispatch(showLoader());
    const authToken = apiCubic.getAuthToken();

    const { data, error } = await requester({
      method: 'post',
      url: `${apiCubic.host}${apiCubic.endpoints.pinRecovery}`,
      appendParams: false,
      extendHeadersWithSession: false,
      headers: { Authorization: `Basic ${authToken}` },
      data: {
        login: email || `0${phone}`,
        loginType: email ? 2 : 1,
        contractCode: contractNumber,
        language: INIT_UI_LANGUAGE,
      },
    });

    if (error) throw new Error(error.message);

    // pin recovery requires 2 steps, so need to back to page that cause pin recovery
    // and delete this path from history
    history.go(-2);
    dispatch(showToast({ options: { toastText: data.payload.userMessage } }));
  } catch (error) {
    console.error(error);
    const message = get(error.response, ['data', 'message'], error.message);

    dispatch(
      showModal('INFO', {
        onModalBack() {
          dispatch(hideModal());
          errorModal.returnFocusTo && SpatialNavigationWrapper.focus(errorModal.returnFocusTo);
        },
        message,
      }),
    );
  } finally {
    dispatch(hideLoader());
  }
};

const onFetchProfileSuccess = (data) => ({
  type: types.FETCH_SUCCESS,
  payload: data,
});

const onFetchProfileError = (error) => ({
  type: types.FETCH_ERROR,
  payload: { error },
});

export const fetchBalance = () => async (dispatch) => {
  try {
    dispatch({ type: types.FETCH_BALANCE });

    const data = await requester({
      method: 'get',
      url: `${api.host}${api.endpoints.balance}`,
      appendParams: false,
    });

    dispatch({
      type: types.FETCH_BALANCE_SUCCESS,
      payload: data,
    });
  } catch (error) {
    dispatch({
      type: types.FETCH_BALANCE_ERROR,
      payload: error,
    });
  }
};

export const setIsUsingDefaultPin = (payload) => (dispatch) => {
  storage.set('isUsingDefaultPin', payload);
  dispatch({ type: types.SET_IS_USING_DEFAUL_PIN, payload });
};

export const universalLogin =
  ({ phone, otp, userFlag, type, errorModal = {}, onSuccess }) =>
  async (dispatch) => {
    const authToken = apiCubic.getAuthToken();

    const data = {
      mobilePhone: `0${phone}`,
      deviceType: Platform.getDeviceRegistrationType(),
      deviceVendor: Platform.getBrand(),
      deviceModel: Platform.getModelName(),
      language: INIT_UI_LANGUAGE,
      otp,
    };

    try {
      dispatch(showLoader());
      const result = await requester({
        method: 'post',
        url: `${apiCubic.host}${apiCubic.endpoints.universalLogin}`,
        appendParams: false,
        extendHeadersWithSession: false,
        headers: { Authorization: `Basic ${authToken}` },
        data,
      });

      if (result.error) {
        const customError = new Error(result.error.message);
        customError.code = result.error.code;
        throw customError;
      }

      const { payload } = result.data;

      innerAnalyticsVolia.postEvent({
        eventGroup: 'sign in screen',
        eventName: type === 'login' ? 'sign in via phone' : 'sign up via phone',
        eventValue: 'success',
        addInfo: `0${phone}`,
      });

      if (payload.ottCustomerId) {
        await dispatch(
          loginUser({
            loginType: LOGIN_TYPES.code,
            code: payload.ottCustomerId,
            userFlag,
            errorModal,
            onSuccess,
            withLoader: false,
          }),
        );
      }
    } catch (error) {
      console.error(error);
      const message = get(error, ['response', 'data', 'message'], error.message);
      const errorCode = get(error, ['response', 'data', 'code'], error.code);

      innerAnalyticsVolia.postEvent({
        eventGroup: 'sign in screen',
        eventName: type === 'login' ? 'sign in via phone' : 'sign up via phone',
        eventValue: errorCode,
        addInfo: `0${phone}`,
      });

      dispatch(
        showModal(MODAL_TYPES.INFO, {
          onModalBack() {
            dispatch(hideModal());
            errorModal.returnFocusTo && SpatialNavigationWrapper.focus(errorModal.returnFocusTo);
          },
          message,
        }),
      );
    } finally {
      dispatch(hideLoader());
    }
  };

export const checkLoginData = () => async (dispatch, getState) => {
  const loginData = loginDataService.get();
  let { userFlag, phone } = loginData || {};
  const profile = getProfileData(getState());

  if (!userFlag) {
    const { data } = await apiCubic.post({
      endpoint: apiCubic.endpoints.checkIfLifecell,
      data: { contractCode: profile.contractNumber },
    });

    userFlag = data.payload?.userFlag;
    phone = data.payload?.phone || null;
    loginDataService.set({ phone, userFlag });
  }

  dispatch(setLoginData({ userFlag, phone }));
};

export const setLoginData = (payload) => ({ type: types.SET_LOGIN_DATA, payload });
